import { richTextListTypes } from 'modules/editor';
import { CustomEditor } from 'shared/components';
import {
  Editor,
  Element as SlateElement,
  Node,
  Point,
  Range,
  Transforms,
} from 'slate';

export function isListItem(element: unknown): element is ListItemElement {
  return SlateElement.isElement(element) && element.type === 'li';
}

export function isListElement(
  element: unknown,
): element is BulletedListElement | NumberedListElement {
  return (
    SlateElement.isElement(element) &&
    (element.type === 'ul' || element.type === 'ol')
  );
}

export function isBlockActive(
  editor: CustomEditor,
  variant: RichTextElementVariant | RichTextLeafVariant,
) {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === variant,
    }),
  );

  return !!match;
}

export function isMarkActive(
  editor: CustomEditor,
  variant: RichTextElementVariant | RichTextLeafVariant,
) {
  const marks = Editor.marks(editor);

  if (!marks) return false;

  if (typeof variant === 'string') {
    return marks[variant as keyof Omit<CustomText, 'text'>] === true;
  }

  return false;
}

export function toggleMark(
  editor: CustomEditor,
  variant: RichTextElementVariant | RichTextLeafVariant,
) {
  const isActive = isMarkActive(editor, variant);

  return isActive
    ? Editor.removeMark(editor, variant)
    : Editor.addMark(editor, variant, true);
}

export function toggleBlock(
  editor: CustomEditor,
  variant: RichTextElementVariant | RichTextLeafVariant,
) {
  const isActive = isBlockActive(editor, variant);
  const isList = richTextListTypes.includes(variant);

  Editor.withoutNormalizing(editor, () => {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        richTextListTypes.includes(n.type),
      split: true,
    });
    const newProperties: Partial<SlateElement> = {
      type: isActive
        ? 'paragraph'
        : isList
          ? 'li'
          : (variant as RichTextElementVariant),
    };

    Transforms.setNodes<SlateElement>(editor, newProperties);

    if (!isActive && isList) {
      Transforms.wrapNodes(editor, {
        type: variant === 'ul' ? 'ul' : 'ol',
        children: [],
      });
    }
  });
}

function getSelectedEmptyListType(editor: CustomEditor) {
  if (!editor.selection || !Range.isCollapsed(editor.selection)) return;

  const listItemEntry = Editor.above(editor, {
    at: editor.selection,
    match: (node) =>
      !Editor.isEditor(node) &&
      SlateElement.isElement(node) &&
      isListItem(node),
  });

  if (!listItemEntry) return;

  const [listItemNode, listItemPath] = listItemEntry;
  if (!isListItem(listItemNode) || Editor.string(editor, listItemPath) !== '') {
    return;
  }

  const parentListEntry = Editor.above(editor, {
    at: listItemPath,
    match: (node) => !Editor.isEditor(node) && isListElement(node),
  });

  if (!parentListEntry) return;

  const [parentListNode] = parentListEntry;
  return isListElement(parentListNode) ? parentListNode.type : undefined;
}

function shouldDeleteExitList(editor: CustomEditor) {
  if (!editor.selection || !Range.isCollapsed(editor.selection)) return false;

  const listItemEntry = Editor.above(editor, {
    at: editor.selection,
    match: (node) =>
      !Editor.isEditor(node) &&
      SlateElement.isElement(node) &&
      isListItem(node),
  });

  if (!listItemEntry) return false;

  const [, listItemPath] = listItemEntry;

  return (
    Editor.string(editor, listItemPath) === '' &&
    Point.equals(editor.selection.anchor, Editor.start(editor, listItemPath))
  );
}

function shouldConvertDashShortcutToBulletedList(editor: CustomEditor) {
  if (!editor.selection || !Range.isCollapsed(editor.selection)) return false;

  const blockEntry = Editor.above(editor, {
    at: editor.selection,
    match: (node) =>
      !Editor.isEditor(node) &&
      SlateElement.isElement(node) &&
      node.type === 'paragraph',
  });

  if (!blockEntry) return false;

  const [blockNode, blockPath] = blockEntry;
  if (!SlateElement.isElement(blockNode) || blockNode.type !== 'paragraph') {
    return false;
  }

  const isDashOnlyBlock = Editor.string(editor, blockPath) === '-';
  const isCursorAtEnd = Point.equals(
    editor.selection.anchor,
    Editor.end(editor, blockPath),
  );

  return isDashOnlyBlock && isCursorAtEnd;
}

function isGhostListState(editor: CustomEditor) {
  if (Editor.string(editor, []) !== '') return false;
  if (editor.children.length !== 1) return false;

  const [firstChild] = editor.children;
  if (!isListElement(firstChild)) return false;

  return !firstChild.children.some((child) => isListItem(child));
}

function resetGhostListState(editor: CustomEditor) {
  if (!isGhostListState(editor)) return false;

  Editor.withoutNormalizing(editor, () => {
    for (let index = editor.children.length - 1; index >= 0; index -= 1) {
      Transforms.removeNodes(editor, { at: [index] });
    }

    Transforms.insertNodes(
      editor,
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
      { at: [0] },
    );
    Transforms.select(editor, Editor.start(editor, [0]));
  });

  return true;
}

export function withLists(editor: CustomEditor) {
  const {
    deleteBackward,
    deleteForward,
    deleteFragment,
    insertBreak,
    insertText,
    normalizeNode,
  } = editor;

  editor.insertText = (text) => {
    if (text === ' ' && shouldConvertDashShortcutToBulletedList(editor)) {
      const { selection } = editor;

      if (!selection) return;

      const blockEntry = Editor.above(editor, {
        at: selection,
        match: (node) =>
          !Editor.isEditor(node) &&
          SlateElement.isElement(node) &&
          node.type === 'paragraph',
      });

      if (!blockEntry) return;

      const [, blockPath] = blockEntry;

      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, blockPath),
          focus: selection.anchor,
        },
      });
      toggleBlock(editor, 'ul');
      return;
    }

    insertText(text);
  };

  editor.deleteBackward = (unit) => {
    const selectedListType = getSelectedEmptyListType(editor);

    if (selectedListType && shouldDeleteExitList(editor)) {
      toggleBlock(editor, selectedListType);
      return;
    }

    deleteBackward(unit);
    resetGhostListState(editor);
  };

  editor.deleteForward = (unit) => {
    const selectedListType = getSelectedEmptyListType(editor);

    if (selectedListType && shouldDeleteExitList(editor)) {
      toggleBlock(editor, selectedListType);
      return;
    }

    deleteForward(unit);
    resetGhostListState(editor);
  };

  editor.deleteFragment = (direction) => {
    deleteFragment(direction);
    resetGhostListState(editor);
  };

  editor.insertBreak = () => {
    const selectedListType = getSelectedEmptyListType(editor);

    if (selectedListType) {
      toggleBlock(editor, selectedListType);
      return;
    }

    insertBreak();
  };

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (isListItem(node) && path.length === 1) {
      if (Editor.string(editor, path) === '') {
        Transforms.setNodes<SlateElement>(
          editor,
          { type: 'paragraph' },
          { at: path },
        );
        return;
      }

      Transforms.wrapNodes(
        editor,
        {
          type: 'ul',
          children: [],
        },
        { at: path },
      );
      return;
    }

    if (isListElement(node)) {
      if (node.children.length === 0) {
        if (path.length === 1) {
          Editor.withoutNormalizing(editor, () => {
            Transforms.removeNodes(editor, { at: path });
            Transforms.insertNodes(
              editor,
              {
                type: 'paragraph',
                children: [{ text: '' }],
              },
              { at: path },
            );
          });
          return;
        }

        Transforms.removeNodes(editor, { at: path });
        return;
      }

      for (const [child, childPath] of Node.children(editor, path)) {
        if (!isListItem(child)) {
          if (Editor.string(editor, path) === '') {
            Transforms.unwrapNodes(editor, { at: path });
            return;
          }

          Transforms.wrapNodes(
            editor,
            {
              type: 'li',
              children: [],
            },
            { at: childPath },
          );
          return;
        }
      }
    }

    normalizeNode(entry);
  };

  return editor;
}
